import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFilter, FiUser, FiMapPin, FiCalendar, FiClock, FiSearch, FiCheckCircle, FiXCircle, FiSmartphone, FiMonitor, FiUsers, FiAlertCircle, FiEdit, FiInbox, FiAlertTriangle, FiRewind, FiFileText, FiSend } from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { MapContainer, TileLayer, Marker, Circle, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { toast } from 'react-toastify';

import 'leaflet/dist/leaflet.css';

import { PageComponent, DatePeriodFilter, getDateParams, Select, Card, EditPointModal, MultiSelect } from '../../components';
import PointDetailsModal from '../../components/PointDetailsModal';
import AnomalyCard from './AnomalyCard';
import { ExecutedPointsSkeleton, OverviewCardSkeleton } from '../../components/Skeletons';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import '../CompanyManager/style.css';
import './style.css';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const parseTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
};

function MapBounds({ stations, records }) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const bounds = L.latLngBounds();
        let hasPoints = false;

        if (stations && stations.length > 0) {
            stations.forEach(station => {
                if (station.lat && station.lng) {
                    bounds.extend([station.lat, station.lng]);
                    hasPoints = true;
                }
            });
        }

        if (records && records.length > 0) {
            records.forEach(record => {
                if (record.latitude && record.longitude) {
                    bounds.extend([parseFloat(record.latitude), parseFloat(record.longitude)]);
                    hasPoints = true;
                }
            });
        }

        if (hasPoints && bounds.isValid()) {
            const timer = setTimeout(() => {
                if (map && map._container) {
                     map.fitBounds(bounds, { padding: [50, 50], animate: false });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [map, stations, records]);

    return null;
}

function PointCardMenu({ onEdit }) {
    const [isOpen, setIsOpen] = useState(false);
    const permissions = usePermission('time_keeping');

    return (
        <div style={{ position: 'relative' }}>
            <button 
                className="tko-menu-trigger" 
                onClick={() => setIsOpen(!isOpen)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            >
                ⋮
            </button>
            {isOpen && (
                <div className="tko-menu-dropdown">
                    {permissions.canEdit && (
                        <button className="tko-menu-item" onMouseDown={(e) => { e.preventDefault(); onEdit(); }}>
                            <FiEdit size={14} />
                            Editar
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function OverviewCard({ data, onEdit }) {
    const lastRecord = useMemo(() => {
        if (!data.records || data.records.length === 0) return null;
        return data.records[data.records.length - 1];
    }, [data.records]);

    const isWorking = data.records && data.records.length % 2 !== 0;

    const formatDistance = (value) => {
        if (!value) return 'Não calculado';
        const distInKm = parseFloat(value);
        if (distInKm < 1) return `${Math.round(distInKm * 1000)}m`;
        return `${distInKm.toFixed(3)}km`;
    };

    // Extract unique stations from records for map
    const uniqueStations = useMemo(() => {
        const stations = new Map();
        if (data.records) {
             data.records.forEach(record => {
                if (record.station_latitude && record.station_longitude) {
                    const key = `${record.station_latitude}_${record.station_longitude}`;
                    if (!stations.has(key)) {
                        stations.set(key, {
                            lat: parseFloat(record.station_latitude),
                            lng: parseFloat(record.station_longitude),
                            name: record.station_name,
                            radius: record.fence_radius_km ? record.fence_radius_km * 1000 : 0
                        });
                    }
                }
            });
        }
        return Array.from(stations.values());
    }, [data.records]);

    const mapCenter = useMemo(() => {
        if (uniqueStations.length > 0) {
            return [uniqueStations[0].lat, uniqueStations[0].lng];
        }
        if (lastRecord && lastRecord.latitude) {
             return [parseFloat(lastRecord.latitude), parseFloat(lastRecord.longitude)];
        }
        return [-15.793889, -47.882778];
    }, [uniqueStations, lastRecord]);

    const workedStationsText = useMemo(() => {
        if (!data.records || data.records.length === 0) return 'Desconhecido';
        
        const stations = new Set();
        data.records.forEach(r => {
            if (r.station_name) {
                stations.add(r.station_name);
            }
        });

        if (stations.size === 0) return 'Desconhecido';
        return Array.from(stations).join(', ');
    }, [data.records]);

    return (
        <Card className="tko-point-card">
            <div className="tko-point-card-header">
                <div className="tko-employee-header">
                    <div>
                        <span className="tko-employee-name">{data.employee_name}</span>
                        <div className="tko-employee-meta">
                            <span><FiUser size={12} /> {data.registration_number || '-'}</span>
                            <span>•</span>
                            <span><FiUser size={12} /> {data.position_name}</span>
                            <span>•</span>
                            <span>{data.records.length} Registros</span>
                        </div>
                    </div>
                    <PointCardMenu onEdit={onEdit} />
                </div>
                <div className="tko-employee-meta">
                     <span>{data.journey_name} {data.journey && data.journey.start_time && `- ${data.journey.start_time.substring(0,5)} As ${data.journey.end_time.substring(0,5)}`}</span>
                </div>
                <div className="tko-journey-tags">
                    <span className="tko-journey-tag date">
                        <FiCalendar size={12} />
                        {data.competence_date ? new Date(data.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' }) : '-'}
                    </span>
                    {lastRecord && (
                        <span className={`tko-journey-tag status ${isWorking ? 'entry' : 'exit'}`}>
                            {isWorking ? 'Retorno Pausa' : 'Saída'}
                        </span>
                    )}
                </div>
            </div>

            <div className="tko-point-card-body">
                <div className="tko-record-info">
                    <div className="tko-photo-circle">
                         {lastRecord && lastRecord.photo ? (
                            <img src={lastRecord.photo} alt={data.employee_name} />
                        ) : (
                            <div className="tko-photo-placeholder">
                                <FiUser size={24} />
                            </div>
                        )}
                    </div>
                    <div className="tko-record-details">
                        <div className={`tko-status-indicator ${isWorking ? 'online' : 'offline'}`}>
                            <span style={{width: 8, height: 8, borderRadius: '50%', background: isWorking ? '#16a34a' : '#64748b', display: 'inline-block'}}></span>
                            {isWorking ? 'On-Line' : 'Offline'}
                        </div>
                        {lastRecord ? (
                            <>
                                <div className="tko-record-row">
                                    <strong>Registro:</strong> {data.competence_date ? new Date(data.competence_date + 'T00:00:00').toLocaleDateString('pt-BR') : ''} {lastRecord.time}
                                </div>
                                <div className="tko-record-row">
                                    <strong>Local:</strong> {workedStationsText}
                                </div>
                                <div className="tko-record-row">
                                    <strong>Distância:</strong> {formatDistance(lastRecord.distance)}
                                </div>
                            </>
                        ) : (
                            <div className="tko-record-row" style={{ fontStyle: 'italic', color: '#999' }}>
                                Sem registros hoje
                            </div>
                        )}
                    </div>
                </div>
                <div className="tko-map-container" style={{background: '#fff'}}>
                    {(uniqueStations.length > 0 || (data.records && data.records.length > 0)) ? (
                        <div className="tko-map-wrapper" style={{ margin: '12px', width: 'calc(100% - 24px)', height: 'calc(100% - 24px)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                             <MapContainer 
                                center={mapCenter} 
                                zoom={15} 
                                scrollWheelZoom={true}
                                dragging={true}
                                zoomControl={false}
                                style={{ height: '100%', width: '100%', zIndex: 0 }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <MapBounds stations={uniqueStations} records={data.records} />
                                
                                {/* Station Markers and Fences */}
                                {uniqueStations.map((station, idx) => (
                                    <React.Fragment key={`station-${idx}`}>
                                        <Marker position={[station.lat, station.lng]}>
                                            <Tooltip direction="bottom" offset={[0, 10]} opacity={1} permanent>
                                                {station.name}
                                            </Tooltip>
                                        </Marker>
                                        {station.radius > 0 && (
                                            <Circle 
                                                center={[station.lat, station.lng]} 
                                                radius={station.radius} 
                                                pathOptions={{ color: 'var(--primary-color)', fillColor: 'var(--primary-color)', fillOpacity: 0.1 }} 
                                            />
                                        )}
                                    </React.Fragment>
                                ))}

                                {/* Point Markers */}
                                {data.records.map((record, idx) => (
                                    record.latitude && record.longitude ? (
                                        <Marker 
                                            key={idx} 
                                            position={[parseFloat(record.latitude), parseFloat(record.longitude)]}
                                        >
                                            <Tooltip direction="bottom" offset={[0, 10]} opacity={1} permanent>
                                                {record.time}
                                            </Tooltip>
                                        </Marker>
                                    ) : null
                                ))}
                            </MapContainer>
                        </div>
                    ) : (
                         <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', flexDirection: 'column', gap: 8 }}>
                             <FiMapPin size={24} />
                             <span style={{ fontSize: '0.75rem' }}>Sem localização</span>
                         </div>
                    )}
                </div>
            </div>
        </Card>
    );
}

const StatCard = ({ title, value, icon: Icon, color, footer }) => (
    <Card className="tko-stat-card">
        <div className="tko-stat-card-content">
            <div className="tko-stat-card-icon" style={{ color: color || '#64748b', backgroundColor: `${color || '#64748b'}15` }}>
                {Icon && <Icon size={22} />}
            </div>
            <div className="tko-stat-card-info">
                <div className="tko-stat-card-value" style={{ color: color || 'var(--text-color)' }}>{value}</div>
                <div className="tko-stat-card-title">{title}</div>
            </div>
        </div>
        {footer && <div className="tko-stat-card-footer">{footer}</div>}
    </Card>
);

const JustificationCard = ({ stats }) => {
        const { accepted, rejected, total } = stats || { accepted: 0, rejected: 0, total: 0 };
        const responded = accepted + rejected;
        
        // Percentages based on RESPONDED items
        const acceptedPercent = responded > 0 ? Math.round((accepted / responded) * 100) : 0;
        const rejectedPercent = responded > 0 ? Math.round((rejected / responded) * 100) : 0;
        
        // Completion rate based on TOTAL items
        const completionPercent = total > 0 ? Math.round((responded / total) * 100) : 0;

        const acceptedData = [
            { value: acceptedPercent },
            { value: 100 - acceptedPercent }
        ];

        const rejectedData = [
            { value: rejectedPercent },
            { value: 100 - rejectedPercent }
        ];

        const totalData = [
            { value: completionPercent },
            { value: 100 - completionPercent }
        ];

        return (
            <Card className="tko-justification-card compact">
                <div className="tko-sidebar-header">
                    <FiFileText size={18} />
                    <span>Justificativas</span>
                </div>
                
                <div className="tko-justification-content">
                    <div className="tko-justification-top">
                        <div className="tko-gauge-container">
                            <div className="tko-gauge-header">
                                <span className="tko-gauge-title">Aceitas</span>
                                <span className="tko-gauge-badge green">
                                    <FiCheckCircle size={10} /> {accepted.toLocaleString()}
                                </span>
                            </div>
                            <div className="tko-gauge-wrapper">
                                <ResponsiveContainer width="100%" height={80}>
                                    <PieChart>
                                        <Pie
                                            data={acceptedData}
                                            cx="50%"
                                            cy="100%"
                                            startAngle={180}
                                            endAngle={0}
                                            innerRadius={30}
                                            outerRadius={40}
                                            paddingAngle={0}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            <Cell fill="#10b981" />
                                            <Cell fill="#e2e8f0" />
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="tko-gauge-value">{acceptedPercent}%</div>
                            </div>
                        </div>

                        <div className="tko-gauge-container">
                            <div className="tko-gauge-header">
                                <span className="tko-gauge-title">Recusadas</span>
                                <span className="tko-gauge-badge red">
                                    <FiXCircle size={10} /> {rejected.toLocaleString()}
                                </span>
                            </div>
                            <div className="tko-gauge-wrapper">
                                <ResponsiveContainer width="100%" height={80}>
                                    <PieChart>
                                        <Pie
                                            data={rejectedData}
                                            cx="50%"
                                            cy="100%"
                                            startAngle={180}
                                            endAngle={0}
                                            innerRadius={30}
                                            outerRadius={40}
                                            paddingAngle={0}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            <Cell fill="#ef4444" />
                                            <Cell fill="#e2e8f0" />
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="tko-gauge-value">{rejectedPercent}%</div>
                            </div>
                        </div>
                    </div>

                    <div className="tko-justification-main">
                        <div className="tko-main-chart-wrapper">
                            <ResponsiveContainer width="100%" height={140}>
                                <PieChart>
                                    <Pie
                                        data={totalData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={55}
                                        outerRadius={65}
                                        paddingAngle={0}
                                        dataKey="value"
                                        stroke="none"
                                        startAngle={90}
                                        endAngle={-270}
                                    >
                                        <Cell fill="#0ea5e9" />
                                        <Cell fill="#e2e8f0" />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="tko-main-chart-value">{completionPercent}%</div>
                        </div>
                        <div className="tko-footer-text">
                            <FiSend className="text-blue" />
                            <span>{responded.toLocaleString()} Justificativas Respondidas</span>
                        </div>
                    </div>
                </div>
            </Card>
        );
    };

export default function TimeKeepingOverview() {
    const permissions = usePermission('time_keeping');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
    const [pointsData, setPointsData] = useState([]);
    const [initialLoad, setInitialLoad] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    // Filters State
    const [periodMode, setPeriodMode] = useState('daily');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });
    
    const [serviceStations, setServiceStations] = useState([]);
    const [selectedServiceStation, setSelectedServiceStation] = useState('all');

    const [positions, setPositions] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState('all');

    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('all');

    const [stats, setStats] = useState({
        totalEmployees: 0,
        presentToday: 0,
        absentToday: 0,
        pendingAdjustments: 0,
        date: ''
    });

    const [justificationStats, setJustificationStats] = useState({
        accepted: 0,
        rejected: 0,
        total: 0
    });

    // Edit Modal State
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [selectedPointForDetails, setSelectedPointForDetails] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    const handleEditPoint = (point) => {
        setSelectedPoint(point);
        setEditModalOpen(true);
    };

    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const getFilterParams = () => {
        const params = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
        return {
            startDate: params.startDate || null,
            endDate: params.endDate || null,
            serviceStationId: selectedServiceStation,
            positionId: selectedPosition,
            employeeId: selectedEmployee
        };
    };

    const filterParams = useMemo(() => getFilterParams(), [periodMode, currentDate, customPeriod, selectedServiceStation, selectedPosition, selectedEmployee]);

    const handleSaveAdjustment = () => {
        fetchPoints();
    };

    useEffect(() => {
        (async () => {
            try {
                const data = await Api.getCompanies();
                const list = Array.isArray(data) ? data : (data.data || []);
                setCompanies(list);
                setCompanyFilterValues(['all']);
            } catch (error) {
                toast.error('Erro ao carregar empresas.');
            }
        })();
    }, []);

    useEffect(() => {
        if (companies.length === 0) return;
        const allValue = 'all';
        const idsWithoutAll = companyFilterValues.filter(v => v !== allValue);
        const selectedIds = companyFilterValues.includes(allValue) ? companies.map(c => String(c.id)) : idsWithoutAll;
        const singleId = selectedIds.length === 1 ? selectedIds[0] : '';
        const selected = companies.find(c => String(c.id) === String(singleId));
        setCompany(selected || null);
        loadFilterOptions(selectedIds);
    }, [companyFilterValues, companies]);

    const loadFilterOptions = async (selectedIds) => {
        try {
            const ids = Array.isArray(selectedIds) && selectedIds.length > 0 ? selectedIds.map(id => Number(id)) : companies.map(c => c.id);
            const [stationsList, positionsList, employeesList] = await Promise.all([
                Promise.all(ids.map(id => Api.getServiceStations(id))),
                Promise.all(ids.map(id => Api.getPositions(id))),
                Promise.all(ids.map(id => Api.getEmployees(id))),
            ]);
            const stationsMerged = stationsList.flat();
            const positionsMerged = positionsList.flat();
            const employeesMerged = employeesList.flat();
            setServiceStations(stationsMerged.map(s => ({ value: s.id, label: s.name })));
            setPositions(positionsMerged.map(p => ({ value: p.id, label: p.name })));
            setEmployees(employeesMerged.map(e => ({ value: e.id, label: e.name })));
        } catch (error) {
            toast.error('Erro ao carregar filtros.');
        }
    };

    const cacheRef = useRef(new Map());

    const fetchPoints = useCallback(async () => {
        if (companies.length === 0) return;

        setLoading(true);
        setPointsData([]);

        try {
            const dp = getDateParams(periodMode, currentDate);
            const params = {};
            if (dp.startDate) params.startDate = dp.startDate;
            if (dp.endDate) params.endDate = dp.endDate;
            if (selectedServiceStation) params.serviceStationId = selectedServiceStation;
            if (selectedPosition) params.positionId = selectedPosition;
            if (selectedEmployee) params.employeeId = selectedEmployee;

            const allValue = 'all';
            const idsWithoutAll = companyFilterValues.filter(v => v !== allValue);
            const selectedIds = companyFilterValues.includes(allValue) ? companies.map(c => String(c.id)) : idsWithoutAll;

            const key = `${JSON.stringify(selectedIds)}|${JSON.stringify(params)}`;
            if (cacheRef.current.has(key)) {
                const cached = cacheRef.current.get(key);
                setPointsData(cached);
            } else {
                const ids = selectedIds.length > 0 ? selectedIds.map(id => Number(id)) : companies.map(c => c.id);
                const responses = await Promise.all(ids.map(async (id) => {
                    const r = await Api.getExecutedPoints(id, params);
                    const arr = (r && r.data) ? r.data : [];
                    return arr.map(item => ({ ...item, company_id: item.company_id ?? id }));
                }));
                const pointsMerged = responses.flat();
                setPointsData(pointsMerged);
                cacheRef.current.set(key, pointsMerged);
            }

            const uniqueEmployeeIds = new Set((cacheRef.current.get(key) || []).map(p => p.employee_id));
            const aggregatedStats = {
                totalEmployees: employees.length,
                presentToday: uniqueEmployeeIds.size,
                absentToday: Math.max(employees.length - uniqueEmployeeIds.size, 0),
                pendingAdjustments: 0,
                date: dp.endDate || ''
            };
            setStats(aggregatedStats);
            setJustificationStats({ accepted: 0, rejected: 0, total: 0 });
            
        } catch (error) {
            console.error('Error fetching points:', error);
            toast.error('Erro ao buscar pontos executados.');
        } finally {
            setLoading(false);
            setInitialLoad(false);
            setHasLoadedOnce(true);
        }
    }, [companyFilterValues, companies, periodMode, currentDate, selectedServiceStation, selectedPosition, selectedEmployee, employees.length]);

    useEffect(() => {
        const t = setTimeout(() => {
            fetchPoints();
        }, 300);
        return () => clearTimeout(t);
    }, [fetchPoints]);

    const { inService, outService } = useMemo(() => {
        const inService = [];
        const outService = [];

        pointsData.forEach(point => {
            const isWorking = point.records && point.records.length % 2 !== 0;
            
            if (isWorking) {
                inService.push(point);
            } else {
                outService.push(point);
            }
        });

        return { inService, outService };
    }, [pointsData]);

    const anomalyBuckets = useMemo(() => {
        const entryDelay = [];
        const intervalDelay = [];
        const earlyExit = [];
        const outsideFence = [];

        pointsData.forEach(point => {
            const records = point.records || [];
            if (records.length === 0) return;

            const journey = point.journey;
            if (!journey) return;

            let start = 0, end = 0, breakStart = 0, breakEnd = 0;
            let rulesEnabled = true;

            if (journey.work_days) {
                const date = point.competence_date ? new Date(point.competence_date + 'T00:00:00') : new Date();
                const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const dayName = dayMap[date.getDay()];
                const rules = journey.work_days[dayName];
                if (rules && rules.enabled) {
                    start = parseTime(rules.start);
                    end = parseTime(rules.end);
                    breakStart = rules.breakStart ? parseTime(rules.breakStart) : 0;
                    breakEnd = rules.breakEnd ? parseTime(rules.breakEnd) : 0;
                } else {
                    rulesEnabled = false;
                }
            } else {
                if (journey.start_time && journey.end_time) {
                    start = parseTime(journey.start_time);
                    end = parseTime(journey.end_time);
                    breakStart = journey.break_start_time ? parseTime(journey.break_start_time) : 0;
                    breakEnd = journey.break_end_time ? parseTime(journey.break_end_time) : 0;
                } else {
                    rulesEnabled = false;
                }
            }

            if (!rulesEnabled) return;

            const isNightShift = start > end;
            let normStart = start;
            let normEnd = isNightShift ? end + 1440 : end;
            let normBreakEnd = breakEnd;
            if (isNightShift) {
                if (breakEnd < start) normBreakEnd += 1440;
            }

            const getNormTime = (record) => {
                const t = parseTime(record.time);
                if (!record.date || !point.competence_date) {
                    if (isNightShift && t < start - 300) return t + 1440;
                    return t;
                }
                if (record.date > point.competence_date) return t + 1440;
                return t;
            };

            // Entry delay: first record later than start + 10
            if (records.length > 0) {
                const firstRecordTime = getNormTime(records[0]);
                if (firstRecordTime > normStart + 10) entryDelay.push(point);
            }

            // Interval delay: third record later than breakEnd + 10
            if (records.length >= 3 && normBreakEnd > 0) {
                const thirdRecordTime = getNormTime(records[2]);
                if (thirdRecordTime > normBreakEnd + 10) intervalDelay.push(point);
            }

            // Early exit: even number of records and last before end, excluding lunch window
            if (records.length % 2 === 0) {
                const lastTime = getNormTime(records[records.length - 1]);
                if (normBreakEnd > 0) {
                    if (!(lastTime >= (breakStart > 0 ? breakStart - 20 : normBreakEnd - 20) && lastTime <= normBreakEnd)) {
                        if (lastTime < normEnd) earlyExit.push(point);
                    }
                } else {
                    if (lastTime < normEnd) earlyExit.push(point);
                }
            }

            // Outside fence: any record not inside any assigned station fence
            const hasOutside = records.some(r => {
                if (!r.latitude || !r.longitude) return false;
                const lat = parseFloat(r.latitude);
                const lon = parseFloat(r.longitude);
                if (!point.assigned_stations || point.assigned_stations.length === 0) return true;
                const insideAny = point.assigned_stations.some(st => {
                    const sLat = parseFloat(st.latitude);
                    const sLon = parseFloat(st.longitude);
                    const radius = parseFloat(st.fence_radius_km);
                    const dist = calculateDistance(lat, lon, sLat, sLon);
                    return dist <= radius;
                });
                return !insideAny;
            });
            if (hasOutside) outsideFence.push(point);
        });

        return {
            entryDelay,
            intervalDelay,
            earlyExit,
            outsideFence
        };
    }, [pointsData]);

    const calculatedStats = useMemo(() => {
        let entryDelay = 0;
        let breakDelay = 0;
        let earlyDeparture = 0;
        let neverRegistered = 0;

        if (!pointsData) return { entryDelay, breakDelay, earlyDeparture, neverRegistered };

        pointsData.forEach(point => {
            const records = point.records || [];
            
            if (records.length === 0) {
                neverRegistered++;
                return; 
            }

            const journey = point.journey;
            if (!journey) return;

            // Determine Schedule
            let start = 0, end = 0, breakStart = 0, breakEnd = 0;
            let rulesEnabled = true;

            if (journey.work_days) {
                // Weekly Schedule
                const date = point.competence_date ? new Date(point.competence_date + 'T00:00:00') : new Date();
                const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const dayName = dayMap[date.getDay()];
                const rules = journey.work_days[dayName];

                if (rules && rules.enabled) {
                    start = parseTime(rules.start);
                    end = parseTime(rules.end);
                    breakStart = rules.breakStart ? parseTime(rules.breakStart) : 0;
                    breakEnd = rules.breakEnd ? parseTime(rules.breakEnd) : 0;
                } else {
                    rulesEnabled = false;
                }
            } else {
                // Fixed/Cyclic Schedule
                if (journey.start_time && journey.end_time) {
                    start = parseTime(journey.start_time);
                    end = parseTime(journey.end_time);
                    breakStart = journey.break_start_time ? parseTime(journey.break_start_time) : 0;
                    breakEnd = journey.break_end_time ? parseTime(journey.break_end_time) : 0;
                } else {
                    rulesEnabled = false;
                }
            }

            if (rulesEnabled) {
                 // Detect Night Shift
                 const isNightShift = start > end;
                 
                 // Normalize Schedule Times
                 let normStart = start;
                 let normEnd = isNightShift ? end + 1440 : end;
                 let normBreakEnd = breakEnd;
                 
                 // Adjust Break for Night Shift
                 if (isNightShift) {
                     if (breakEnd < start) normBreakEnd += 1440;
                 }

                 // Helper to normalize record time based on date
                 const getNormTime = (record) => {
                     const t = parseTime(record.time);
                     if (!record.date || !point.competence_date) {
                         // Fallback logic if date is missing
                         if (isNightShift && t < start - 300) return t + 1440; // Assume next day if time is much earlier than start
                         return t;
                     }
                     
                     // Compare string dates: YYYY-MM-DD
                     if (record.date > point.competence_date) {
                          return t + 1440;
                     }
                     return t;
                 };

                 // 1. Entry Delay (Tolerance 10m)
                 if (records.length > 0) {
                     const firstRecord = getNormTime(records[0]);
                     if (firstRecord > normStart + 10) entryDelay++;
                 }

                 // 2. Break Delay (Atraso no Intervalo)
                 if (records.length >= 3) { 
                      const returnFromBreak = getNormTime(records[2]);
                      if (normBreakEnd > 0) {
                          if (returnFromBreak > normBreakEnd + 10) breakDelay++;
                      }
                 }

                 // 3. Early Departure (Antecipação)
                 if (records.length > 0 && records.length % 2 === 0) {
                     const lastRecord = records[records.length - 1];
                     const lastTime = getNormTime(lastRecord);
                     
                     if (normBreakEnd > 0) {
                         if (lastTime > normBreakEnd && lastTime < normEnd) earlyDeparture++;
                     } else {
                         if (lastTime < normEnd) earlyDeparture++;
                     }
                 }
            }
        });

        return { entryDelay, breakDelay, earlyDeparture, neverRegistered };
    }, [pointsData]);

    if (!permissions.canView) {
        return (
            <PageComponent authenticatedPage={true}>
                <div className="container">
                    <div className="manager-header">
                        <div className="manager-header-title">
                            <div className="manager-header-icon">
                                <FiMonitor size={22} />
                            </div>
                            <div>
                                <h1>Acesso Negado</h1>
                                <p>Você não tem permissão para visualizar esta página.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </PageComponent>
        );
    }

    if (initialLoad) {
        return (
            <PageComponent authenticatedPage>
                <div className="container">
                    <ExecutedPointsSkeleton />
                </div>
            </PageComponent>
        );
    }

    const handleViewPoint = (point) => {
        setSelectedPointForDetails(point);
        setIsDetailsModalOpen(true);
    };

    return (
        <PageComponent authenticatedPage>
            <div className="container">
                <div className="manager-header">
                    <div className="manager-header-title">
                        <div className="manager-header-icon">
                            <FiMonitor size={22} />
                        </div>
                        <div>
                            <h1>Monitoramento em Tempo Real</h1>
                            <p>Acompanhe quem está em serviço e fora de serviço.</p>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
            <Card className="manager-filters-card tko-filters">
                    <div className="executed-points-filters" style={{ marginBottom: 0 }}>
                        <div className="filter-group filter-group-period">
                            <DatePeriodFilter
                                periodMode={periodMode}
                                setPeriodMode={setPeriodMode}
                                currentDate={currentDate}
                                setCurrentDate={setCurrentDate}
                                customRange={customPeriod}
                                setCustomRange={setCustomPeriod}
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Empresa</label>
                        <MultiSelect
                            id="companyFilter"
                            options={[{ value: 'all', label: 'TODAS' }, ...companies.map(c => ({
                                value: String(c.id),
                                label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`
                            }))]}
                            value={companyFilterValues}
                            onChange={(newValues) => {
                                const allValue = 'all';
                                const currentSelected = companyFilterValues;
                                const isAllSelected = newValues.includes(allValue);
                                const wasAllSelected = currentSelected.includes(allValue);

                                let nextValues;

                                if (isAllSelected && !wasAllSelected) {
                                  const allIds = companies.map(c => String(c.id));
                                  nextValues = [allValue, ...allIds];
                                } else if (!isAllSelected && wasAllSelected) {
                                  nextValues = [];
                                } else if (wasAllSelected && newValues.length < currentSelected.length) {
                                  const remaining = newValues.filter(v => v !== allValue);
                                  nextValues = remaining;
                                } else {
                                  const specificIds = newValues.filter(v => v !== allValue);
                                  const allIds = companies.map(c => String(c.id));
                                  if (specificIds.length === allIds.length && allIds.length > 0) {
                                    nextValues = [allValue, ...specificIds];
                                  } else {
                                    nextValues = specificIds;
                                  }
                                }

                                setCompanyFilterValues(nextValues);
                            }}
                            searchable
                        />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Posto de Serviço</label>
                            <Select
                                placeholder="Todos"
                                options={[{ value: 'all', label: 'TODOS' }, ...serviceStations]}
                                value={selectedServiceStation}
                                onChange={(e) => setSelectedServiceStation(e.target.value)}
                                searchable
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Cargo</label>
                            <Select
                                placeholder="Todos"
                                options={[{ value: 'all', label: 'TODOS' }, ...positions]}
                                value={selectedPosition}
                                onChange={(e) => setSelectedPosition(e.target.value)}
                                searchable
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Funcionário</label>
                            <Select
                                placeholder="Todos"
                                options={[{ value: 'all', label: 'TODOS' }, ...employees]}
                                value={selectedEmployee}
                                onChange={(e) => setSelectedEmployee(e.target.value)}
                                searchable
                            />
                        </div>
                    </div>
                </Card>

                <div className="tko-overview-grid">
                    <div className="tko-kpi-column">
                        <StatCard 
                            title="Funcionários Totais" 
                            value={stats.totalEmployees || employees.length || '-'} 
                            icon={FiUsers} 
                        />
                        <StatCard 
                            title="Presentes" 
                            value={loading ? '-' : stats.presentToday} 
                            icon={FiCheckCircle} 
                            color="#16a34a" 
                        />
                        <StatCard 
                            title="Faltas" 
                            value={loading ? '-' : stats.absentToday} 
                            icon={FiAlertCircle} 
                            color="#dc2626" 
                        />
                        <StatCard 
                            title="Justificativas Pendentes" 
                            value={loading ? '-' : stats.pendingAdjustments} 
                            icon={FiEdit} 
                            color="#3b82f6" 
                        />
                    </div>

                    <Card className="tko-sidebar-card compact">
                        <div className="tko-sidebar-header">
                            <FiClock size={18} />
                            <span>Estatísticas de Ponto</span>
                        </div>
                        
                        <div className="tko-sidebar-content">
                            <div className="tko-stats-row">
                                <div className="tko-stats-info">
                                    <div className="tko-stats-icon-bg green">
                                        <FiCheckCircle size={16} />
                                    </div>
                                    <span className="tko-stats-label">Registros</span>
                                </div>
                                <span className="tko-stats-value">{loading ? '-' : (inService.length + outService.length)}</span>
                            </div>

                            <div className="tko-stats-row">
                                <div className="tko-stats-info">
                                    <div className="tko-stats-icon-bg yellow">
                                        <FiAlertTriangle size={16} />
                                    </div>
                                    <span className="tko-stats-label">Nunca Registrou</span>
                                </div>
                                <span className="tko-stats-value">{loading ? '-' : calculatedStats.neverRegistered}</span>
                            </div>

                            <div className="tko-stats-divider" />

                            <div className="tko-stats-row">
                                <div className="tko-stats-info">
                                    <div className="tko-stats-icon-bg orange">
                                        <FiClock size={16} />
                                    </div>
                                    <span className="tko-stats-label">Atrasos</span>
                                </div>
                                <span className="tko-stats-value">{loading ? '-' : calculatedStats.entryDelay}</span>
                            </div>

                            <div className="tko-stats-row">
                                <div className="tko-stats-info">
                                    <div className="tko-stats-icon-bg blue">
                                        <FiClock size={16} />
                                    </div>
                                    <span className="tko-stats-label">Intervalo</span>
                                </div>
                                <span className="tko-stats-value">{loading ? '-' : calculatedStats.breakDelay}</span>
                            </div>

                            <div className="tko-stats-row">
                                <div className="tko-stats-info">
                                    <div className="tko-stats-icon-bg purple">
                                        <FiRewind size={16} />
                                    </div>
                                    <span className="tko-stats-label">Antecipação de Saída</span>
                                </div>
                                <span className="tko-stats-value">{loading ? '-' : calculatedStats.earlyDeparture}</span>
                            </div>
                        </div>
                    </Card>
                    <JustificationCard stats={justificationStats} />
                </div>

                <div className="overview-main-container full-width">
                    <Card className="tko-overview-card-container">
                        <div className="tko-sidebar-header">
                            <FiMonitor size={18} />
                            <span>Análise em Tempo Real de Funcionários</span>
                        </div>
                        
                        <div className="tko-split-view" style={{ margin: 0, padding: 0, minHeight: '300px', maxHeight: '600px', overflow: 'auto' }}>
                            {/* In Service Column */}
                            <div className="tko-status-col">
                                <div className="tko-status-header in-service">
                                    <FiCheckCircle />
                                    Em Serviço
                                    <span className="tko-status-badge">{loading ? '-' : inService.length}</span>
                                </div>
                                {loading ? (
                                    <>
                                        <OverviewCardSkeleton />
                                        <OverviewCardSkeleton />
                                    </>
                                ) : inService.length > 0 ? (
                                    inService.map(point => (
                                        <OverviewCard 
                                            key={point.id} 
                                            data={point} 
                                            onEdit={() => handleEditPoint(point)} 
                                        />
                                    ))
                                ) : (
                                    <div className="tko-empty-state">
                                        <div className="tko-empty-icon">
                                            <FiInbox size={32} />
                                        </div>
                                        <p className="tko-empty-text">Ninguém trabalhando</p>
                                        <span className="tko-empty-subtext">Não há funcionários em serviço no momento.</span>
                                    </div>
                                )}
                            </div>

                            {/* Out of Service Column */}
                            <div className="tko-status-col">
                                <div className="tko-status-header out-service">
                                    <FiXCircle />
                                    Fora Serviço
                                    <span className="tko-status-badge">{loading ? '-' : outService.length}</span>
                                </div>
                                {loading ? (
                                    <>
                                        <OverviewCardSkeleton />
                                        <OverviewCardSkeleton />
                                    </>
                                ) : outService.length > 0 ? (
                                    outService.map(point => (
                                        <OverviewCard 
                                            key={point.id} 
                                            data={point} 
                                            onEdit={() => handleEditPoint(point)} 
                                        />
                                    ))
                                ) : (
                                    <div className="tko-empty-state">
                                        <div className="tko-empty-icon">
                                            <FiInbox size={32} />
                                        </div>
                                        <p className="tko-empty-text">Ninguém fora de serviço</p>
                                        <span className="tko-empty-subtext">Não há funcionários fora de serviço.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    <div className="tko-anomaly-grid">
                        <AnomalyCard
                            title="Atraso na Entrada"
                            icon={FiClock}
                            color="#dc2626"
                            type="entry_delay"
                            data={anomalyBuckets.entryDelay}
                            onViewPoint={handleViewPoint}
                            onEditPoint={handleEditPoint}
                        />
                        <AnomalyCard
                            title="Atraso no Intervalo"
                            icon={FiClock}
                            color="#ca8a04"
                            type="interval_delay"
                            data={anomalyBuckets.intervalDelay}
                            onViewPoint={handleViewPoint}
                            onEditPoint={handleEditPoint}
                        />
                        <AnomalyCard
                            title="Antecipação na Saída"
                            icon={FiRewind}
                            color="#2563eb"
                            type="early_exit"
                            data={anomalyBuckets.earlyExit}
                            onViewPoint={handleViewPoint}
                            onEditPoint={handleEditPoint}
                        />
                        <AnomalyCard
                            title="Fora da Cerca"
                            icon={FiMapPin}
                            color="#dc2626"
                            type="outside_fence"
                            data={anomalyBuckets.outsideFence}
                            onViewPoint={handleViewPoint}
                            onEditPoint={handleEditPoint}
                        />
                    </div>
                </div>
            </div>

            <PointDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                pointData={selectedPointForDetails}
            />

            <EditPointModal
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                pointData={selectedPoint}
                companyId={selectedPoint?.company_id || company?.id}
                onSave={handleSaveAdjustment}
            />
        </PageComponent>
    );
}
